/**
 * 日報の報告文（LINE へ貼る下書き）を組み立てる。DAILY-WORKFLOW-4 / LINE-REPORT-1。
 * 載せる数値の定義は `docs/02` §2.4（報告文だけの別名・別指標）。
 *
 * ## ★この関数が受け取れるものを絞ってある
 *
 * 引数は**営業日・サーバが計算した数値（`ReportFigures`）・振り返りの本文**だけ。
 * 従業員氏名も時給も人件費も、そもそも渡せない（絶対ルール1）。
 * 「混ぜないように気をつける」ではなく、**混ぜられない形**にしてある。
 *
 * ## ★OCR の読取候補は使わない
 *
 * 呼び出し側は `daily_sales` など**業務へ登録された値**から作った数値を渡す（`docs/05` §3.1 の日報の注記）。
 *
 * ## ★未登録・出せない値を 0 にしない
 *
 * 売上が無い日は「未登録」、出せない値は「—」と書く。0 と書くと、読んだ人には
 * 「売上0円の日」と区別がつかない（CLAUDE.md 絶対ルール5 と同じ考え方）。
 *
 * ## ★送信はしない
 *
 * 文面を作るだけ。コピーしたことを「報告済み」とは扱わない（`docs/05` §3.1）。
 */
import { ratio } from "./money.js";
import { type DailySales, summarizeMonth } from "./sales.js";

/** 出せる値は `value`、出せない値は `value = null` と理由。★0 で埋めない */
export interface Figure {
  value: number | null;
  reason: string | null;
}

const some = (value: number): Figure => ({ value, reason: null });
const none = (reason: string): Figure => ({ value: null, reason });

/** 報告文に出せる数値。★この形以外は受け取れない（給与系のフィールドを持たない） */
export interface ReportFigures {
  /** 当日。売上が未登録なら `null`（0 ではない） */
  day: {
    groups: number;
    guests: number;
    inclTax: number;
    exclTax: Figure;
    /** 税込。報告文だけ（docs/02 §2.4） */
    perGuestInclTax: Figure;
  } | null;
  /** 月初〜当日 */
  cumulative: {
    groups: Figure;
    guests: Figure;
    exclTax: Figure;
    perGuestInclTax: Figure;
    /** 当日までの営業日数で割る（§2.2 の日商平均とは別） */
    dailyAverage: Figure;
    /** 速報・棚卸前（§3.2 の原価率とは別）。小数（0.516 = 51.6%） */
    costRate: Figure;
  };
  /** ④ 翌日の見込み（docs/02 §2.4 ④） */
  forecast: ForecastFigures;
  /**
   * ① の利益・人件費率（docs/02 §2.4）。★**`can_view_payroll` の相手にだけサーバが組み立てる。**
   * 権限が無いときは**キー自体が無い**（`null` を入れない。あるかないかで宛先を分ける）
   */
  payroll?: PayrollFigures;
}

/** ① の利益・人件費率。★店舗合計の率と営業利益だけ。人件費の金額・目標FL・目標人件費率は持たない（絶対ルール1） */
export interface PayrollFigures {
  /** §7 日次営業利益（速報）。損益カードの当日と同じ値 */
  dayProfit: Figure;
  /** §7.1 累計の営業利益（速報）。損益カードの累計と同じ値 */
  cumulativeProfit: Figure;
  /** 日次人件費合計 ÷ 日次売上税抜（小数） */
  dayLaborRate: Figure;
  /** 売上が入力済みの日の日次人件費合計の和 ÷ 同じ日の売上税抜の和（小数） */
  cumulativeLaborRate: Figure;
}

/** ① の入力。営業利益は §7 / §7.1 の値（ここで計算し直さない） */
export interface PayrollInput {
  dayProfit: Figure;
  cumulativeProfit: Figure;
  /** 月初〜当日で売上が入力済みの日（§7.1 の累計に入る日）。`labor` は日次人件費合計。出せなければ `null` */
  entered: readonly { businessDate: string; salesExclTax: number; labor: number | null }[];
  /** `labor` が出せないときの理由 */
  laborReason: string | null;
}

/** ④ の入力。★見込み2つは店長が入れた値（未入力は `null`＝0 ではない）。目標と予算は設定値 */
export interface ForecastInput {
  /** 翌日仕入れ見込み額（円・税込） */
  purchaseEstimate: number | null;
  /** 明日売上見込み（円・税抜） */
  salesEstimate: number | null;
  /** ③ 明日の予約の組数・人数・コース人数・シフト人数。店長が入れた値をそのまま写す（計算しない）。未入力は `null` */
  reservationGroups: number | null;
  reservationGuests: number | null;
  courseGuests: number | null;
  shiftStaff: number | null;
  /** 月間売上目標（税抜）。未設定は `null`＋理由 */
  monthlySalesTarget: Figure;
  /** 目標原価率（小数。0.29 = 29%）。未設定は `null`＋理由 */
  costRateBudget: Figure;
}

export interface ForecastFigures {
  purchaseEstimate: Figure;
  salesEstimate: Figure;
  /**
   * ③ の4つ。★入力値のまま（`Figure` ではない＝「—」も理由も持たない。未入力は報告文でも空欄のまま）。
   * 理由の一覧（`reportFigureReasons`）には入らない
   */
  reservationGroups: number | null;
  reservationGuests: number | null;
  courseGuests: number | null;
  shiftStaff: number | null;
  /** 月初〜当日の原価仕入 ＋ 翌日仕入れ見込み額 */
  totalPurchase: Figure;
  /** 月初〜当日の売上税抜 ＋ 明日売上見込み */
  totalSales: Figure;
  /** 総仕入れ額 ÷ 総売上見込み（小数） */
  costRate: Figure;
  /** 月間売上目標 × 目標原価率（原価率予算） − 総仕入れ額。★マイナスもそのまま */
  remainingPurchase: Figure;
}

export interface ReportMonthInput {
  /** 当月の全日（日付順）。未入力の日は 0 で入っている */
  days: readonly DailySales[];
  recordedDates: ReadonlySet<string>;
  /** `business_calendar` が揃っているか。揃っていなければ営業日数に頼る値を出さない */
  calendarKnown: boolean;
  calendarReason: string | null;
  /** 日付 → その日の原価仕入（§7.1 と同じ範囲） */
  purchasesByDate: ReadonlyMap<string, number>;
}

/** 足し算。出せない側があれば、その項目（理由つき）をそのまま返す。★0 で埋めない */
function sum(a: Figure, b: Figure): Figure {
  if (a.value === null) return a;
  if (b.value === null) return b;
  return some(a.value + b.value);
}

/**
 * ④ 翌日の見込み（docs/02 §2.4 ④）。★「総」は月初〜当日の累計に見込みを足したもの（月末予測ではない）。
 * @param salesToDate 月初〜当日の売上税抜。累計が出せないなら理由つきの `null`
 * @param purchasesToDate 月初〜当日の原価仕入。累計が出せないなら `null`
 */
function buildForecast(f: ForecastInput, salesToDate: Figure, purchasesToDate: number | null): ForecastFigures {
  const purchaseEstimate = f.purchaseEstimate === null ? none("翌日の仕入見込みが未入力です") : some(f.purchaseEstimate);
  const salesEstimate = f.salesEstimate === null ? none("明日の売上見込みが未入力です") : some(f.salesEstimate);
  // ★仕入が0円＝未登録と区別できない。見込みだけの低い値が「予算内」に見えるので出さない（速報累計原価率と同じ）
  const purchaseToDate =
    purchasesToDate === null
      ? none(salesToDate.reason ?? "月初から当日までの累計が出せません")
      : purchasesToDate === 0
        ? none("当日までの仕入が登録されていません")
        : some(purchasesToDate);
  const totalPurchase = sum(purchaseToDate, purchaseEstimate);
  const totalSales = sum(salesToDate, salesEstimate);

  let costRate: Figure;
  if (totalPurchase.value === null) costRate = totalPurchase;
  else if (totalSales.value === null) costRate = totalSales;
  else {
    const r = ratio(totalPurchase.value, totalSales.value);
    costRate = r === null ? none("総売上見込みが0円です") : some(r);
  }

  let remainingPurchase: Figure;
  if (totalPurchase.value === null) remainingPurchase = totalPurchase;
  else if (f.monthlySalesTarget.value === null) remainingPurchase = f.monthlySalesTarget;
  else if (f.costRateBudget.value === null) remainingPurchase = f.costRateBudget;
  else remainingPurchase = some(f.monthlySalesTarget.value * f.costRateBudget.value - totalPurchase.value);

  return {
    purchaseEstimate,
    salesEstimate,
    reservationGroups: f.reservationGroups,
    reservationGuests: f.reservationGuests,
    courseGuests: f.courseGuests,
    shiftStaff: f.shiftStaff,
    totalPurchase,
    totalSales,
    costRate,
    remainingPurchase,
  };
}

/**
 * ① の利益・人件費率（docs/02 §2.4）。率の計算はここだけ。★0 で埋めない
 * @param date 当日（`entered` から当日の行を探す）
 */
export function buildPayrollFigures(input: PayrollInput | { unavailableReason: string }, date: string): PayrollFigures {
  if ("unavailableReason" in input) {
    const f = none(input.unavailableReason);
    return { dayProfit: f, cumulativeProfit: f, dayLaborRate: f, cumulativeLaborRate: f };
  }
  const laborReason = input.laborReason ?? "日次人件費を計算できません";
  const rate = (rows: PayrollInput["entered"]): Figure => {
    if (rows.some((r) => r.labor === null)) return none(laborReason);
    let labor = 0;
    let sales = 0;
    for (const r of rows) {
      labor += r.labor ?? 0;
      sales += r.salesExclTax;
    }
    const v = ratio(labor, sales);
    return v === null ? none("売上が0円です") : some(v);
  };
  const today = input.entered.filter((r) => r.businessDate === date);
  return {
    dayProfit: input.dayProfit,
    cumulativeProfit: input.cumulativeProfit,
    dayLaborRate: today.length === 0 ? none("売上が未登録です") : rate(today),
    cumulativeLaborRate: input.entered.length === 0 ? none("月初から当日まで売上が未登録です") : rate(input.entered),
  };
}

function dayExcl(month: ReportMonthInput | null, date: string, reason: string): Figure {
  if (month === null) return none(reason);
  const d = month.days.find((x) => x.businessDate === date);
  return d === undefined ? none("当月の日付に含まれません") : some(d.exclTax);
}

/** 月初〜`date` の原価仕入の合計（`docs/02` §2.4）。速報累計原価率の分子と、④の総仕入れ額の月初〜当日分は同じ値 */
function purchasesThrough(purchasesByDate: ReadonlyMap<string, number>, date: string): number {
  let purchases = 0;
  for (const [d, amount] of purchasesByDate) if (d <= date) purchases += amount;
  return purchases;
}

/**
 * 速報原価率（棚卸前）＝ 月初〜`date` の原価仕入 ÷ 月初〜`date` の売上税抜（`docs/02` §2.4）。
 * ★報告文（`buildReportFigures`）と損益カード（`findProfitLoss`）の**唯一の式**。
 * 仕入は売上が未入力の日の分も足す（§7.1 の累計とは範囲が違う）。小数（0.516 = 51.6%）
 */
export function provisionalCostRate(input: {
  date: string;
  days: readonly DailySales[];
  recordedDates: ReadonlySet<string>;
  purchasesByDate: ReadonlyMap<string, number>;
}): Figure {
  const { date, days, recordedDates, purchasesByDate } = input;
  const through = days.filter((d) => d.businessDate <= date);
  if (!through.some((d) => recordedDates.has(d.businessDate))) {
    return none("月初から当日まで売上が未登録です");
  }
  const purchases = purchasesThrough(purchasesByDate, date);
  // ★仕入が0円＝「仕入未登録」と区別がつかない。0.0% と書かず出さない（§2.4）
  if (purchases === 0) return none("当日までの仕入が登録されていません");
  const r = ratio(purchases, summarizeMonth(through).exclTax);
  return r === null ? none("売上が0円です") : some(r);
}

/**
 * @param registered 当日の登録済みの売上（税率に依らない値）。未登録なら `null`
 * @param month 月のデータ。税率が解決できないなど集められなければ `{ unavailableReason }`
 */
export function buildReportFigures(input: {
  date: string;
  registered: { inclTax: number; guests: number; groups: number } | null;
  month: ReportMonthInput | { unavailableReason: string };
  forecast: ForecastInput;
}): ReportFigures {
  const { registered, month, date, forecast } = input;
  const monthOk = "days" in month ? month : null;
  const reason = "days" in month ? "" : month.unavailableReason;

  const day: ReportFigures["day"] =
    registered === null
      ? null
      : {
          ...registered,
          exclTax: dayExcl(monthOk, date, reason),
          perGuestInclTax:
            registered.guests === 0 ? none("客数が0です") : some(registered.inclTax / registered.guests),
        };

  const cumulativeNone = (why: string): Pick<ReportFigures, "cumulative" | "forecast"> => ({
    cumulative: {
      groups: none(why),
      guests: none(why),
      exclTax: none(why),
      perGuestInclTax: none(why),
      dailyAverage: none(why),
      costRate: none(why),
    },
    forecast: buildForecast(forecast, none(why), null),
  });
  if (monthOk === null) return { day, ...cumulativeNone(reason) };

  const through = monthOk.days.filter((d) => d.businessDate <= date);
  if (!through.some((d) => monthOk.recordedDates.has(d.businessDate))) {
    return { day, ...cumulativeNone("月初から当日まで売上が未登録です") };
  }
  const total = summarizeMonth(through);
  let groups = 0;
  for (const d of through) groups += d.groups;

  const dailyAverage = !monthOk.calendarKnown
    ? none(monthOk.calendarReason ?? "営業カレンダーが未整備です")
    : total.dailyAverage === null
      ? none("営業日が0日です")
      : some(total.dailyAverage);
  return {
    day,
    cumulative: {
      groups: some(groups),
      guests: some(total.guests),
      exclTax: some(total.exclTax),
      perGuestInclTax:
        total.guests === 0 ? none("客数が0です") : some(total.inclTax / total.guests),
      dailyAverage,
      costRate: provisionalCostRate({
        date,
        days: monthOk.days,
        recordedDates: monthOk.recordedDates,
        purchasesByDate: monthOk.purchasesByDate,
      }),
    },
    forecast: buildForecast(forecast, some(total.exclTax), purchasesThrough(monthOk.purchasesByDate, date)),
  };
}

/** 出せなかった項目の理由（重複なし）。★文面には入れず、画面で添える */
export function reportFigureReasons(figures: ReportFigures): string[] {
  const all: Figure[] = [
    ...(figures.day === null ? [] : [figures.day.exclTax, figures.day.perGuestInclTax]),
    ...Object.values(figures.cumulative),
    // ★③の4つは数字（または null）で理由を持たない。`Figure` だけを拾う
    ...(Object.values(figures.forecast).filter((v) => typeof v === "object" && v !== null) as Figure[]),
    ...(figures.payroll === undefined ? [] : Object.values(figures.payroll)),
  ];
  return [...new Set(all.flatMap((f) => (f.reason === null ? [] : [f.reason])))];
}

const MISSING = "—";
const n = (value: number): string => Math.round(value).toLocaleString("ja-JP");
/** 万円表示は 0.1万円（1,000円）単位の四捨五入。表示だけで計算には使わない */
/** 万円表示（0.1万円単位の四捨五入）。画面の入力欄の換算も同じ式を使う */
export const manText = (yen: number): string => `${(Math.round(yen / 1000) / 10).toFixed(1)}万円`;
const man = (f: Figure): string => (f.value === null ? MISSING : manText(f.value));
const percent = (f: Figure): string => (f.value === null ? MISSING : `${(Math.round(f.value * 1000) / 10).toFixed(1)}%`);
const perGuest = (f: Figure): string => (f.value === null ? "@—" : `@${n(f.value)}円`);
const count = (f: Figure): string => (f.value === null ? MISSING : n(f.value));

const WEEKDAYS = "日月火水木金土";
/** `YYYY-MM-DD` ＋ offset 日 → 「10月1日(木)」。★暦で足す（月末→翌月1日・曜日も） */
function monthDay(date: string, offsetDays: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + offsetDays));
  return `${String(t.getUTCMonth() + 1)}月${String(t.getUTCDate())}日(${WEEKDAYS[t.getUTCDay()] ?? ""})`;
}

export function buildDailyReportText(input: {
  businessDate: string;
  figures: ReportFigures;
  /** 保存済みの振り返り。未保存・空なら `null` */
  comment: string | null;
  /** 末尾の定型文（住所・電話・理念）。未設定なら `null`（空行も足さない） */
  footer?: string | null;
}): string {
  const { day, cumulative: c, payroll: p } = input.figures;
  const lines = ["🔲営業日報🔲", "", "", `🔸${monthDay(input.businessDate, 0)}`, "", "【①売上】"];

  if (day === null) {
    lines.push("\u3000売上：未登録");
  } else {
    lines.push(`\u3000${n(day.groups)}組\u3000${n(day.guests)}名様${perGuest(day.perGuestInclTax)}`);
    lines.push(`売上\u3000税込\u3000${man(some(day.inclTax))}`);
    lines.push(`\u3000\u3000\u3000税抜\u3000${man(day.exclTax)}`);
  }
  // ★payroll が無い（権限が無い）ときは行ごと出さない。「—」も出さない
  if (p !== undefined) lines.push(`利益\u3000${man(p.dayProfit)}（速報）`, `人件費率\u3000${percent(p.dayLaborRate)}`);

  lines.push("", "🔵累計");
  lines.push(`${count(c.groups)}組\u3000${count(c.guests)}名様${perGuest(c.perGuestInclTax)}`);
  lines.push(`売上\u3000${man(c.exclTax)}`);
  if (p !== undefined) lines.push(`累計利益\u3000${man(p.cumulativeProfit)}（速報）`);
  lines.push(`日商平均\u3000${man(c.dailyAverage)}`);
  lines.push(`原価率\u3000${percent(c.costRate)}（速報・棚卸前）`);
  if (p !== undefined) lines.push(`◎累計人件費率\u3000${percent(p.cumulativeLaborRate)}`);

  lines.push("", "【②ありがとう営業報告日報】");
  lines.push(input.comment === null || input.comment.trim() === "" ? "（未保存）" : input.comment);

  // ③は入力値をそのまま写す（docs/02 §2.4 ③）。★未入力の項目は空欄のまま（店長が手で書き足せる）
  const fc = input.figures.forecast;
  lines.push("", "【③明日ご予約情報共有】", "", monthDay(input.businessDate, 1), "");
  lines.push(
    `${fc.reservationGroups === null ? "\u3000\u3000" : n(fc.reservationGroups)}組\u3000${fc.reservationGuests === null ? "" : n(fc.reservationGuests)}名様`,
    `コース\u3000${fc.courseGuests === null ? "\u3000\u3000" : n(fc.courseGuests)}名様`,
    `シフト数\u3000${fc.shiftStaff === null ? "\u3000" : n(fc.shiftStaff)}名`,
  );
  lines.push("", "【④翌日仕入れ見込み額】", "");
  lines.push(`①\u3000${man(fc.purchaseEstimate)}`, "", `②総仕入れ額\u3000${man(fc.totalPurchase)}`, "");
  lines.push(`③明日売上見込み\u3000${man(fc.salesEstimate)}`, "", `④総売上見込み\u3000${man(fc.totalSales)}`, "");
  lines.push(`⑤原価率見込み\u3000${percent(fc.costRate)}`, "※②÷④✖︎100の計算", "", `⑥残り仕入れ可能額\u3000${man(fc.remainingPurchase)}`);
  // 定型文は最後に空行を1つ挟んで付ける。未設定なら何も付けない（docs/02 §2.4 ③）
  const footer = input.footer ?? null;
  if (footer !== null) lines.push("", footer);
  return lines.join("\n");
}
